import React from 'react';
import { useFormikContext } from 'formik';
import { 
  TextField, 
  MenuItem, 
  FormControl, 
  FormHelperText, 
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
  Box,
  Slider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { BookingFormValues } from '../../pages/BookingPage';
import GridItem from '../common/GridItem';
import GridContainer from '../common/GridContainer';
import ResponsiveTypography from '../common/ResponsiveTypography';

const TripDetailsForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<BookingFormValues>();

  // This would usually come from an API or geocoding service
  const dummyLocations = [
    'JFK Airport',
    'LaGuardia Airport',
    'Newark Liberty International Airport',
    'Manhattan, NY',
    'Brooklyn, NY',
    'Queens, NY',
    'Bronx, NY',
    'Staten Island, NY',
    'Midtown Manhattan',
    'Financial District',
    'Times Square',
    'Grand Central Terminal',
    'Penn Station',
    'Wall Street',
    'Central Park',
  ];

  return (
    <>
      <ResponsiveTypography 
        variant="h5" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          mb: { xs: 2, sm: 3 },
          textAlign: isMobile ? 'center' : 'left' 
        }}
      >
        Trip Details
      </ResponsiveTypography>

      <GridContainer spacing={isMobile ? 2 : 3}>
        <GridItem xs={12}>
          <FormControl component="fieldset" fullWidth>
            <ResponsiveTypography variant="subtitle2" gutterBottom>
              Trip Type
            </ResponsiveTypography>
            <RadioGroup
              row={!isMobile}
              name="tripType"
              value={values.tripType}
              onChange={handleChange}
              sx={{
                justifyContent: isMobile ? 'space-around' : 'flex-start',
                '& .MuiFormControlLabel-root': {
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0
                }
              }}
            >
              <FormControlLabel
                value="one-way"
                control={<Radio />}
                label="One-way"
              />
              <FormControlLabel
                value="round-trip"
                control={<Radio />}
                label="Round-trip"
              />
              <FormControlLabel
                value="hourly"
                control={<Radio />}
                label="Hourly Hire"
              />
            </RadioGroup>
            {touched.tripType && errors.tripType && (
              <FormHelperText error>{errors.tripType}</FormHelperText>
            )}
          </FormControl>
        </GridItem>

        <GridItem xs={12}>
          <Divider sx={{ my: { xs: 1, sm: 2 } }} />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Autocomplete
            id="pickupLocation"
            options={dummyLocations}
            freeSolo
            value={values.pickupLocation}
            onChange={(_, newValue) => {
              setFieldValue('pickupLocation', newValue || '');
            }}
            onBlur={() => setFieldTouched('pickupLocation', true)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pickup Location"
                fullWidth
                error={touched.pickupLocation && Boolean(errors.pickupLocation)}
                helperText={touched.pickupLocation ? errors.pickupLocation : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: isMobile ? '56px' : 'auto'
                  }
                }}
              />
            )}
          />
        </GridItem>

        {values.tripType !== 'hourly' && (
          <GridItem xs={12} md={6}>
            <Autocomplete
              id="dropoffLocation"
              options={dummyLocations}
              freeSolo
              value={values.dropoffLocation}
              onChange={(_, newValue) => {
                setFieldValue('dropoffLocation', newValue || '');
              }}
              onBlur={() => setFieldTouched('dropoffLocation', true)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Drop-off Location"
                  fullWidth
                  error={touched.dropoffLocation && Boolean(errors.dropoffLocation)}
                  helperText={touched.dropoffLocation ? errors.dropoffLocation : ''}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: isMobile ? '56px' : 'auto'
                    }
                  }}
                />
              )}
            />
          </GridItem>
        )}

        <GridItem xs={12}>
          <Divider sx={{ my: { xs: 1, sm: 2 } }} />
        </GridItem>

        <GridItem xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Pickup Date"
              value={values.pickupDate}
              onChange={(date) => setFieldValue('pickupDate', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: touched.pickupDate && Boolean(errors.pickupDate),
                  helperText: touched.pickupDate ? errors.pickupDate as string : '',
                  onBlur: () => setFieldTouched('pickupDate', true),
                  sx: {
                    '& .MuiInputBase-root': {
                      minHeight: isMobile ? '56px' : 'auto'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </GridItem>

        <GridItem xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              label="Pickup Time"
              value={values.pickupTime ? new Date(`2023-01-01T${values.pickupTime}`) : null}
              onChange={(time) => {
                if (time) {
                  const hours = time.getHours().toString().padStart(2, '0');
                  const minutes = time.getMinutes().toString().padStart(2, '0');
                  setFieldValue('pickupTime', `${hours}:${minutes}`);
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: touched.pickupTime && Boolean(errors.pickupTime),
                  helperText: touched.pickupTime ? errors.pickupTime as string : '',
                  onBlur: () => setFieldTouched('pickupTime', true),
                  sx: {
                    '& .MuiInputBase-root': {
                      minHeight: isMobile ? '56px' : 'auto'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </GridItem>

        {values.tripType === 'round-trip' && (
          <>
            <GridItem xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Return Date"
                  value={values.returnDate}
                  onChange={(date) => setFieldValue('returnDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: touched.returnDate && Boolean(errors.returnDate),
                      helperText: touched.returnDate ? errors.returnDate as string : '',
                      onBlur: () => setFieldTouched('returnDate', true),
                      sx: {
                        '& .MuiInputBase-root': {
                          minHeight: isMobile ? '56px' : 'auto'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </GridItem>

            <GridItem xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Return Time"
                  value={values.returnTime ? new Date(`2023-01-01T${values.returnTime}`) : null}
                  onChange={(time) => {
                    if (time) {
                      const hours = time.getHours().toString().padStart(2, '0');
                      const minutes = time.getMinutes().toString().padStart(2, '0');
                      setFieldValue('returnTime', `${hours}:${minutes}`);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: touched.returnTime && Boolean(errors.returnTime),
                      helperText: touched.returnTime ? errors.returnTime as string : '',
                      onBlur: () => setFieldTouched('returnTime', true),
                      sx: {
                        '& .MuiInputBase-root': {
                          minHeight: isMobile ? '56px' : 'auto'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </GridItem>
          </>
        )}

        {values.tripType === 'hourly' && (
          <GridItem xs={12}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <ResponsiveTypography variant="subtitle2" gutterBottom sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                Number of Hours: {values.hours}
              </ResponsiveTypography>
              <Box sx={{ px: 2 }}>
                <Slider
                  name="hours"
                  min={2}
                  max={12}
                  step={1}
                  marks
                  value={values.hours}
                  onChange={(_, newValue) => {
                    setFieldValue('hours', newValue);
                  }}
                  valueLabelDisplay="auto"
                  sx={{
                    '& .MuiSlider-thumb': {
                      height: isMobile ? 24 : 20,
                      width: isMobile ? 24 : 20,
                    },
                    '& .MuiSlider-rail': {
                      height: isMobile ? 8 : 4
                    },
                    '& .MuiSlider-track': {
                      height: isMobile ? 8 : 4
                    }
                  }}
                />
              </Box>
              {touched.hours && errors.hours && (
                <FormHelperText error>{errors.hours}</FormHelperText>
              )}
            </Box>
          </GridItem>
        )}

        <GridItem xs={12}>
          <Divider sx={{ my: { xs: 1, sm: 2 } }} />
        </GridItem>

        <GridItem xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <ResponsiveTypography variant="subtitle2" gutterBottom sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              Number of Passengers: {values.passengers}
            </ResponsiveTypography>
            <Box sx={{ px: 2 }}>
              <Slider
                name="passengers"
                min={1}
                max={20}
                step={1}
                value={values.passengers}
                onChange={(_, newValue) => {
                  setFieldValue('passengers', newValue);
                }}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    height: isMobile ? 24 : 20,
                    width: isMobile ? 24 : 20,
                  },
                  '& .MuiSlider-rail': {
                    height: isMobile ? 8 : 4
                  },
                  '& .MuiSlider-track': {
                    height: isMobile ? 8 : 4
                  }
                }}
              />
            </Box>
          </Box>
          {touched.passengers && errors.passengers && (
            <FormHelperText error>{errors.passengers}</FormHelperText>
          )}
        </GridItem>

        <GridItem xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <ResponsiveTypography variant="subtitle2" gutterBottom sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              Number of Luggage Items: {values.luggage}
            </ResponsiveTypography>
            <Box sx={{ px: 2 }}>
              <Slider
                name="luggage"
                min={0}
                max={20}
                step={1}
                value={values.luggage}
                onChange={(_, newValue) => {
                  setFieldValue('luggage', newValue);
                }}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    height: isMobile ? 24 : 20,
                    width: isMobile ? 24 : 20,
                  },
                  '& .MuiSlider-rail': {
                    height: isMobile ? 8 : 4
                  },
                  '& .MuiSlider-track': {
                    height: isMobile ? 8 : 4
                  }
                }}
              />
            </Box>
          </Box>
          {touched.luggage && errors.luggage && (
            <FormHelperText error>{errors.luggage}</FormHelperText>
          )}
        </GridItem>
      </GridContainer>
    </>
  );
};

export default TripDetailsForm; 